import { dayKeyFromDate, formatMinutesLabel, parseTimeToMinutes } from '@/lib/calendar/dayCalendar'
import { shiftIsoDate, toIsoDate } from '@/lib/calendar/calendarDates'

export const WEEK_STARTS_ON = 1

/**
 * @param {string} anchorDate YYYY-MM-DD
 * @param {number} weekStartsOn 0 = Sunday, 1 = Monday
 */
export function startOfWeekIso(anchorDate, weekStartsOn = WEEK_STARTS_ON) {
  const date = new Date(`${anchorDate}T12:00:00`)
  const day = date.getDay()
  const diff = (day - weekStartsOn + 7) % 7
  date.setDate(date.getDate() - diff)
  return toIsoDate(date)
}

/**
 * @param {string} anchorDate
 * @param {number} weekStartsOn
 * @returns {{ dates: string[], from: string, to: string }}
 */
export function buildWeekDayRange(anchorDate, weekStartsOn = WEEK_STARTS_ON) {
  const start = startOfWeekIso(anchorDate, weekStartsOn)
  const dates = []

  for (let offset = 0; offset < 7; offset += 1) {
    dates.push(shiftIsoDate(start, offset))
  }

  return {
    dates,
    from: start,
    to: shiftIsoDate(start, 7),
  }
}

/**
 * @param {string[]} weekDates
 */
export function formatWeekHeading(weekDates) {
  if (weekDates.length === 0) {
    return ''
  }

  const first = new Date(`${weekDates[0]}T12:00:00`)
  const last = new Date(`${weekDates[weekDates.length - 1]}T12:00:00`)
  const sameYear = first.getFullYear() === last.getFullYear()
  const sameMonth = sameYear && first.getMonth() === last.getMonth()

  const startLabel = first.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  })

  const endLabel = last.toLocaleDateString(undefined, {
    month: sameMonth ? undefined : 'short',
    day: 'numeric',
    year: 'numeric',
  })

  return `${startLabel} – ${endLabel}`
}

/**
 * @param {string} iso
 */
export function appointmentDayFromIso(iso) {
  return iso.slice(0, 10)
}

/**
 * @param {import('@/types/api').Appointment[]} appointments
 * @param {string} dayDate
 */
export function appointmentsForDay(appointments, dayDate) {
  return appointments.filter(
    (appointment) => appointmentDayFromIso(appointment.starts_at) === dayDate,
  )
}

/**
 * @param {Record<string, { open: string, close: string }>} businessHours
 * @param {string[]} weekDates
 */
export function resolveWeekGridHours(businessHours, weekDates) {
  let minOpen = 24 * 60
  let maxClose = 0

  for (const date of weekDates) {
    const hours = businessHours[dayKeyFromDate(date)] ?? { open: '10:00', close: '22:00' }
    minOpen = Math.min(minOpen, parseTimeToMinutes(hours.open))
    maxClose = Math.max(maxClose, parseTimeToMinutes(hours.close))
  }

  return {
    openTime: formatMinutesLabel(minOpen),
    closeTime: formatMinutesLabel(maxClose),
  }
}

/**
 * @param {Record<string, { open: string, close: string }>} businessHours
 * @param {string} dayDate
 */
export function hoursForDay(businessHours, dayDate) {
  return businessHours[dayKeyFromDate(dayDate)] ?? { open: '10:00', close: '22:00' }
}

/**
 * @param {string} dayDate
 * @param {string} today
 */
export function isToday(dayDate, today) {
  return dayDate === today
}

/**
 * @param {string} dayDate
 */
export function formatDayColumnLabel(dayDate) {
  const date = new Date(`${dayDate}T12:00:00`)
  return {
    weekday: date.toLocaleDateString(undefined, { weekday: 'short' }),
    day: date.getDate(),
  }
}
